import { useState, useEffect, useCallback, useRef } from "react";

const STEP_DURATION = 6500;

const steps = [
  {
    id: "hero",
    label: "Welcome",
    caption: "AI-Powered Review Responses",
    subtitle: "StellarReply writes personalised, on-brand responses to your Google reviews — so you never leave a customer waiting.",
  },
  {
    id: "import",
    label: "Import",
    caption: "Import Reviews in One Click",
    subtitle: "Connect your Google listing and pull in your latest reviews instantly. No copy-pasting, no switching tabs.",
  },
  {
    id: "generate",
    label: "AI Response",
    caption: "AI Crafts the Perfect Reply",
    subtitle: "Claude AI analyses the review tone, your business type, and your preferences to write a response that sounds like you.",
  },
  {
    id: "translate",
    label: "Translate",
    caption: "Translate Reviews Instantly",
    subtitle: "Translate English reviews into any language of your choice with one click, powered by Google Cloud.",
  },
  {
    id: "share",
    label: "Share",
    caption: "Share Reviews as Images",
    subtitle: "Turn your best reviews into branded shareable images for social media, websites, and marketing materials.",
  },
  {
    id: "dashboard",
    label: "Dashboard",
    caption: "Track Everything at a Glance",
    subtitle: "See your review stats, response rate, AI-powered insights, and recent activity from one simple dashboard.",
  },
  {
    id: "cta",
    label: "Get Started",
    caption: "Ready to Save Hours Every Week?",
    subtitle: "Join StellarReply today. Early adopter pricing at just \u00A320/month, locked in for life.",
  },
];

const C = {
  bg: "#faf9f6",
  card: "#ffffff",
  border: "#e8e5df",
  text: "#1a1a1a",
  textMuted: "#6b7280",
  textLight: "#9ca3af",
  green: "#16a34a",
  greenLight: "#dcfce7",
  greenBg: "rgba(22,163,74,0.08)",
  orange: "#f97316",
  star: "#f97316",
  emerald: "#34d399",
  navy: "#0b1628",
};

const StarRating = ({ rating, size = 13 }) => (
  <span style={{ display: "inline-flex", gap: 1 }}>
    {[1, 2, 3, 4, 5].map((s) => (
      <span key={s} style={{ color: s <= rating ? C.star : "#ddd", fontSize: size }}>{"\u2605"}</span>
    ))}
  </span>
);

const Badge = ({ text, color = C.green, bg = C.greenLight }) => (
  <span style={{
    fontSize: 9.5, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
    background: bg, color, letterSpacing: 0.2,
  }}>{text}</span>
);

const ActionBtn = ({ text, primary }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 10, fontWeight: 600, padding: "5px 10px", borderRadius: 6,
    background: primary ? C.green : "#fff",
    color: primary ? "#fff" : C.text,
    border: primary ? "none" : `1px solid ${C.border}`,
    cursor: "pointer",
  }}>{text}</span>
);

const MockReviewCard = ({ name, rating, text, status, response, delay = 0, animate = false, children }) => {
  const [visible, setVisible] = useState(!animate);
  useEffect(() => {
    if (animate) {
      const t = setTimeout(() => setVisible(true), delay);
      return () => clearTimeout(t);
    }
  }, [animate, delay]);

  const initial = name ? name[0].toUpperCase() : "?";
  const colors = ["#16a34a", "#2563eb", "#9333ea", "#dc2626", "#d97706"];
  const bgColor = colors[name.charCodeAt(0) % colors.length];

  return (
    <div style={{
      background: C.card, borderRadius: 10, padding: "14px 16px",
      marginBottom: 10, border: `1px solid ${C.border}`,
      opacity: visible ? 1 : 0,
      transform: visible ? "translateY(0)" : "translateY(14px)",
      transition: "all 0.5s cubic-bezier(.22,1,.36,1)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", background: bgColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: "#fff",
          }}>{initial}</div>
          <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>{name}</span>
          <StarRating rating={rating} size={11} />
        </div>
        {status && <Badge text={status} />}
      </div>
      <p style={{ color: C.textMuted, fontSize: 12, margin: "4px 0 0 36px", lineHeight: 1.5 }}>"{text}"</p>
      {response && (
        <div style={{
          margin: "10px 0 0 36px", padding: "10px 12px",
          background: C.greenBg, borderRadius: 8,
          borderLeft: `3px solid ${C.green}`,
        }}>
          <div style={{ fontSize: 10, color: C.green, fontWeight: 600, marginBottom: 4 }}>AI Response</div>
          <p style={{ color: C.text, fontSize: 11.5, margin: 0, lineHeight: 1.5 }}>{response}</p>
        </div>
      )}
      {children}
    </div>
  );
};

const TypingDots = () => {
  const [dots, setDots] = useState(1);
  useEffect(() => {
    const i = setInterval(() => setDots((d) => (d % 3) + 1), 400);
    return () => clearInterval(i);
  }, []);
  return (
    <span style={{ color: C.green, fontSize: 13, fontFamily: "monospace", letterSpacing: 3 }}>
      {".".repeat(dots)}<span style={{ opacity: 0 }}>{".".repeat(3 - dots)}</span>
    </span>
  );
};

// --- Screens ---

const HeroScreen = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", textAlign: "center", padding: 30 }}>
    <div style={{
      width: 56, height: 56, borderRadius: 14,
      background: `linear-gradient(135deg, ${C.green}, #059669)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      marginBottom: 18, boxShadow: "0 8px 24px rgba(22,163,74,0.25)",
    }}>
      <span style={{ fontSize: 26, color: "#fff", lineHeight: 1 }}>{"\u2605"}</span>
    </div>
    <div style={{ color: C.green, fontSize: 11, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 10 }}>StellarReply</div>
    <div style={{ color: C.text, fontSize: 17, fontWeight: 700, marginBottom: 14, lineHeight: 1.3 }}>
      Never Leave a Review<br />Unanswered Again
    </div>
    <div style={{ display: "flex", gap: 24, marginTop: 8 }}>
      {[
        { num: "5x", label: "Faster" },
        { num: "24/7", label: "Always On" },
        { num: "\u00A320", label: "/month" },
      ].map((s) => (
        <div key={s.label} style={{ textAlign: "center" }}>
          <div style={{ color: C.green, fontSize: 22, fontWeight: 800 }}>{s.num}</div>
          <div style={{ color: C.textLight, fontSize: 10, marginTop: 2 }}>{s.label}</div>
        </div>
      ))}
    </div>
  </div>
);

const ImportScreen = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1000);
    const t2 = setTimeout(() => setPhase(2), 2200);
    const t3 = setTimeout(() => setPhase(3), 3400);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div style={{ padding: 18, height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>Reviews</div>
        <button style={{
          background: phase >= 2 ? C.greenLight : C.green,
          color: phase >= 2 ? C.green : "#fff",
          border: "none", borderRadius: 8, padding: "7px 14px",
          fontSize: 11, fontWeight: 700, cursor: "pointer",
          transition: "all 0.4s ease",
          transform: phase === 0 ? "scale(1.05)" : "scale(1)",
          boxShadow: phase === 0 ? "0 0 12px rgba(22,163,74,0.25)" : "none",
        }}>
          {phase === 0 ? "Sync from Google" : phase === 1 ? "Syncing..." : "Synced"}
        </button>
      </div>
      {phase >= 1 && phase < 3 && (
        <div style={{
          background: C.greenBg, borderRadius: 8, padding: "9px 12px",
          marginBottom: 12, border: "1px solid rgba(22,163,74,0.12)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div className="spin" style={{ width: 12, height: 12, border: `2px solid ${C.green}`, borderTopColor: "transparent", borderRadius: "50%" }} />
            <span style={{ color: C.green, fontSize: 11.5 }}>Fetching reviews from Google Places...</span>
          </div>
        </div>
      )}
      {phase >= 3 && (
        <div style={{
          background: C.greenBg, borderRadius: 8, padding: "9px 12px",
          marginBottom: 12, border: "1px solid rgba(22,163,74,0.12)",
        }}>
          <span style={{ color: C.green, fontSize: 11.5, fontWeight: 500 }}>Imported 3 new reviews, skipped 2 duplicates.</span>
        </div>
      )}
      <MockReviewCard name="Sarah M." rating={5} text="Best coffee in Bromley! Always friendly and quick." status="GENERATED" animate delay={2600} />
      <MockReviewCard name="James T." rating={4} text="Great food, lovely atmosphere. A bit slow on weekends." status="GENERATED" animate delay={3100} />
      <MockReviewCard name="Priya K." rating={2} text="Waited 30 mins for a table with a reservation." status="PENDING" animate delay={3600} />
    </div>
  );
};

const GenerateScreen = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 800);
    const t2 = setTimeout(() => setPhase(2), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div style={{ padding: 18, height: "100%" }}>
      <MockReviewCard name="Priya K." rating={2} text="Waited 30 mins for a table with a reservation. Staff didn't seem bothered." status="PENDING" />
      <div style={{
        marginTop: 10, padding: "14px 16px",
        background: phase >= 2 ? C.greenBg : C.card,
        borderRadius: 10, border: `1px solid ${phase >= 2 ? "rgba(22,163,74,0.2)" : C.border}`,
        transition: "all 0.5s ease",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{
            width: 20, height: 20, borderRadius: 5,
            background: `linear-gradient(135deg, ${C.green}, #059669)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 10, color: "#fff", fontWeight: 700,
          }}>S</div>
          <span style={{ color: C.green, fontSize: 12, fontWeight: 700 }}>
            {phase === 0 ? "AI Response" : phase === 1 ? "Generating..." : "AI Response Ready"}
          </span>
          {phase === 1 && <TypingDots />}
        </div>
        {phase >= 2 ? (
          <>
            <p style={{
              color: C.text, fontSize: 11.5, margin: 0, lineHeight: 1.6,
              animation: "fadeInDown 0.6s ease",
            }}>
              Hi Priya, thank you for your honest feedback. I'm really sorry about the wait - that's not the experience we want for our guests, especially with a reservation. I've spoken to the team about improving our table management. We'd love the chance to make it right - please reach out and your next meal is on us. - Tom
            </p>
            <div style={{ display: "flex", gap: 6, marginTop: 12, flexWrap: "wrap", animation: "fadeInDown 0.6s ease" }}>
              <ActionBtn text="Approve" primary />
              <ActionBtn text="Edit" />
              <ActionBtn text="Regenerate" />
              <ActionBtn text="Copy" />
              <ActionBtn text="Skip" />
              <ActionBtn text="Share" />
            </div>
          </>
        ) : phase === 0 ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "10px 0" }}>
            <div style={{
              background: C.green, color: "#fff", borderRadius: 8, padding: "8px 20px",
              fontSize: 12, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 12px rgba(22,163,74,0.2)",
            }}>Generate Response</div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const TranslateScreen = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1200);
    const t2 = setTimeout(() => setPhase(2), 2800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div style={{ padding: 18, height: "100%" }}>
      <div style={{
        background: C.card, borderRadius: 10, padding: "14px 16px",
        marginBottom: 10, border: `1px solid ${C.border}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{
              width: 28, height: 28, borderRadius: "50%", background: "#d97706",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 12, fontWeight: 700, color: "#fff",
            }}>M</div>
            <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>Marco R.</span>
            <StarRating rating={5} size={11} />
          </div>
          <Badge text="GENERATED" />
        </div>
        <p style={{ color: C.textMuted, fontSize: 12, margin: "4px 0 0 36px", lineHeight: 1.5 }}>
          "Fantastic place! The food was excellent and the staff very kind. We will definitely come back!"
        </p>

        {phase >= 1 && (
          <div style={{
            margin: "10px 0 0 36px", padding: "10px 12px",
            background: "#eef2ff", borderRadius: 8,
            borderLeft: "3px solid #6366f1",
            animation: "fadeInDown 0.5s ease",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontSize: 10, color: "#4f46e5", fontWeight: 600 }}>Translated to Spanish</span>
              <span style={{ fontSize: 9, color: "#6366f1", background: "#e0e7ff", padding: "2px 6px", borderRadius: 4 }}>Spanish</span>
            </div>
            {phase >= 2 ? (
              <p style={{ color: C.text, fontSize: 11.5, margin: 0, lineHeight: 1.5, fontStyle: "italic" }}>
                "Lugar fantastico! La comida era excelente y el personal muy amable. Volveremos seguro!"
              </p>
            ) : (
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 0" }}>
                <div className="spin" style={{ width: 12, height: 12, border: "2px solid #6366f1", borderTopColor: "transparent", borderRadius: "50%" }} />
                <span style={{ color: "#4f46e5", fontSize: 11 }}>Translating via Google Cloud...</span>
              </div>
            )}
          </div>
        )}

        {phase >= 2 && (
          <div style={{
            margin: "10px 0 0 36px", padding: "10px 12px",
            background: C.greenBg, borderRadius: 8,
            borderLeft: `3px solid ${C.green}`,
            animation: "fadeInDown 0.5s ease",
          }}>
            <div style={{ fontSize: 10, color: C.green, fontWeight: 600, marginBottom: 4 }}>AI Response</div>
            <p style={{ color: C.text, fontSize: 11.5, margin: 0, lineHeight: 1.5 }}>
              Thank you so much Marco! We're thrilled you enjoyed the food and the service. Looking forward to welcoming you back soon. - Tom
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const ShareScreen = () => {
  const [phase, setPhase] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 1000);
    const t2 = setTimeout(() => setPhase(2), 2500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <div style={{ padding: 18, height: "100%" }}>
      <div style={{ color: C.text, fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Share Review</div>

      <div style={{
        background: C.card, borderRadius: 10, padding: "14px 16px",
        border: `1px solid ${C.border}`, marginBottom: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <div style={{
            width: 28, height: 28, borderRadius: "50%", background: "#2563eb",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 12, fontWeight: 700, color: "#fff",
          }}>S</div>
          <span style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>Sarah M.</span>
          <StarRating rating={5} size={11} />
        </div>
        <p style={{ color: C.textMuted, fontSize: 12, margin: "4px 0 0 36px", lineHeight: 1.5 }}>
          "Best coffee in Bromley! Always friendly and quick."
        </p>
      </div>

      {phase === 0 && (
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
          <div style={{
            background: C.green, color: "#fff", borderRadius: 8, padding: "8px 20px",
            fontSize: 12, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 12px rgba(22,163,74,0.2)",
            transform: "scale(1.05)",
          }}>Share as Image</div>
        </div>
      )}

      {phase >= 1 && (
        <div style={{
          background: "linear-gradient(145deg, #0b1628 0%, #0f2318 100%)",
          borderRadius: 12, padding: "22px 20px",
          border: "1px solid rgba(52,211,153,0.2)",
          animation: "fadeInDown 0.5s ease",
          position: "relative", overflow: "hidden",
        }}>
          <div style={{
            position: "absolute", top: -30, right: -30, width: 100, height: 100,
            background: "radial-gradient(circle, rgba(52,211,153,0.12), transparent)",
            borderRadius: "50%",
          }} />
          <div style={{ display: "flex", alignItems: "center", gap: 2, marginBottom: 12 }}>
            {[1, 2, 3, 4, 5].map((s) => (
              <span key={s} style={{ color: "#facc15", fontSize: 18 }}>{"\u2605"}</span>
            ))}
          </div>
          <p style={{ color: "#e2e8f0", fontSize: 13.5, margin: "0 0 16px 0", lineHeight: 1.7, fontStyle: "italic" }}>
            "Best coffee in Bromley! Always friendly and quick."
          </p>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ color: "#94a3b8", fontSize: 11 }}>- Sarah M.</span>
            <span style={{ color: C.emerald, fontSize: 9.5, fontWeight: 700, letterSpacing: 1.5 }}>STELLARREPLY.COM</span>
          </div>
        </div>
      )}

      {phase >= 2 && (
        <div style={{
          display: "flex", gap: 8, marginTop: 12, justifyContent: "center",
          animation: "fadeInDown 0.4s ease",
        }}>
          <ActionBtn text="Download Image" primary />
          <ActionBtn text="Copy Link" />
        </div>
      )}
    </div>
  );
};

const DashboardScreen = () => {
  const [counters, setCounters] = useState({ reviews: 0, rating: 0, rate: 0 });
  useEffect(() => {
    const duration = 1400;
    const targets = { reviews: 6, rating: 4.17, rate: 100 };
    const start = Date.now();
    const animate = () => {
      const elapsed = Date.now() - start;
      const progress = Math.min(elapsed / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCounters({
        reviews: Math.round(targets.reviews * ease),
        rating: +(targets.rating * ease).toFixed(2),
        rate: Math.round(targets.rate * ease),
      });
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, []);

  return (
    <div style={{ padding: 18, height: "100%" }}>
      <div style={{ display: "flex", gap: 0, marginBottom: 12, background: C.card, borderRadius: 8, border: `1px solid ${C.border}`, overflow: "hidden" }}>
        {["1 Month", "3 Months", "6 Months", "12 Months", "All Time"].map((t) => (
          <span key={t} style={{
            flex: 1, textAlign: "center", fontSize: 8.5, fontWeight: 600, padding: "6px 0",
            background: t === "6 Months" ? C.green : "transparent",
            color: t === "6 Months" ? "#fff" : C.textMuted,
            cursor: "pointer",
          }}>{t}</span>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, marginBottom: 12 }}>
        {[
          { label: "TOTAL REVIEWS", value: counters.reviews, color: C.text },
          { label: "AVG RATING", value: counters.rating, color: C.star, suffix: " \u2605" },
          { label: "RESPONSE RATE", value: `${counters.rate}%`, color: C.green },
          { label: "PENDING", value: 0, color: C.text },
        ].map((s) => (
          <div key={s.label} style={{
            background: C.card, borderRadius: 8, padding: "10px 8px",
            border: `1px solid ${C.border}`,
          }}>
            <div style={{ fontSize: 7.5, color: C.textMuted, fontWeight: 600, letterSpacing: 0.5, marginBottom: 4 }}>{s.label}</div>
            <div style={{ color: s.color, fontSize: 15, fontWeight: 800 }}>{s.value}{s.suffix || ""}</div>
          </div>
        ))}
      </div>

      <div style={{
        background: C.card, borderRadius: 10, padding: "12px 14px",
        border: `1px solid ${C.border}`, marginBottom: 10,
      }}>
        <div style={{ fontSize: 8.5, color: C.textMuted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>WHAT YOUR CUSTOMERS ARE SAYING</div>
        <div style={{ fontSize: 10, color: C.textLight, marginBottom: 8 }}>AI-powered analysis of your recent reviews</div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>Customers love</span>
        </div>
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
          {["Consistent quality", "Friendly staff", "Affordable prices", "Great atmosphere"].map((tag) => (
            <span key={tag} style={{
              fontSize: 9.5, padding: "3px 8px", borderRadius: 99,
              border: `1px solid ${C.green}`, color: C.green, fontWeight: 500,
            }}>{tag}</span>
          ))}
        </div>
        <div style={{
          background: C.bg, borderRadius: 6, padding: "8px 10px",
          fontSize: 10.5, color: C.textMuted, fontStyle: "italic", lineHeight: 1.5,
        }}>
          "Your business is highly praised for consistently excellent service at great prices, with particular strength in making customers feel welcome."
        </div>
        <div style={{ fontSize: 8.5, color: C.textLight, marginTop: 6 }}>Based on 4 reviews - Updates daily</div>
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: 1, background: C.card, borderRadius: 8, padding: "10px 12px", border: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 8.5, color: C.textMuted, fontWeight: 700, letterSpacing: 0.5, marginBottom: 6 }}>RECENT REVIEWS</div>
          {[
            { name: "Mark", time: "22h ago", rating: 1 },
            { name: "Phil Murfitt", time: "29 Jan", rating: 5 },
            { name: "Amy Jones", time: "4 Dec", rating: 5 },
          ].map((r, i) => (
            <div key={i} style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "5px 0",
              borderBottom: i < 2 ? `1px solid ${C.border}` : "none",
              animation: "fadeInDown 0.4s ease",
              animationDelay: `${0.6 + i * 0.15}s`,
              animationFillMode: "both",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{
                  width: 20, height: 20, borderRadius: "50%",
                  background: r.rating >= 4 ? C.green : "#dc2626",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 9, fontWeight: 700, color: "#fff",
                }}>{r.rating}</div>
                <div>
                  <div style={{ fontSize: 10.5, fontWeight: 600, color: C.text }}>{r.name}</div>
                  <div style={{ fontSize: 8.5, color: C.textLight }}>{r.time}</div>
                </div>
              </div>
              <Badge text="GENERATED" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CTAScreen = () => (
  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", textAlign: "center", padding: 30 }}>
    <div style={{
      width: 48, height: 48, borderRadius: 12,
      background: `linear-gradient(135deg, ${C.green}, #059669)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      marginBottom: 16, boxShadow: "0 8px 24px rgba(22,163,74,0.25)",
      fontSize: 22, color: "#fff",
    }}>{"\u2605"}</div>
    <div style={{ color: C.text, fontSize: 17, fontWeight: 700, marginBottom: 8, lineHeight: 1.3 }}>
      Start Responding<br />Smarter Today
    </div>
    <div style={{ color: C.textMuted, fontSize: 12, marginBottom: 20, maxWidth: 280, lineHeight: 1.5 }}>
      Early adopter pricing at {"\u00A3"}20/month, locked in for life. No contracts, cancel anytime.
    </div>
    <div style={{
      background: `linear-gradient(135deg, ${C.green}, #059669)`,
      color: "#fff", borderRadius: 10, padding: "10px 28px",
      fontSize: 13, fontWeight: 800, cursor: "pointer",
      boxShadow: "0 4px 16px rgba(22,163,74,0.25)",
    }}>
      Start Free Trial
    </div>
    <div style={{ color: C.textLight, fontSize: 10, marginTop: 10 }}>No credit card required - Setup in 2 minutes</div>
  </div>
);

const screens = {
  hero: HeroScreen,
  import: ImportScreen,
  generate: GenerateScreen,
  translate: TranslateScreen,
  share: ShareScreen,
  dashboard: DashboardScreen,
  cta: CTAScreen,
};

export default function StellarReplyWalkthrough() {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const intervalRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const elapsedRef = useRef(0);

  const goToStep = useCallback((idx) => {
    setTransitioning(true);
    setTimeout(() => {
      setCurrentStep(idx);
      setProgress(0);
      elapsedRef.current = 0;
      startTimeRef.current = Date.now();
      setTransitioning(false);
    }, 280);
  }, []);

  const nextStep = useCallback(() => {
    goToStep((currentStep + 1) % steps.length);
  }, [currentStep, goToStep]);

  useEffect(() => {
    if (isPaused) return;
    startTimeRef.current = Date.now() - elapsedRef.current;

    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      elapsedRef.current = elapsed;
      const pct = Math.min((elapsed / STEP_DURATION) * 100, 100);
      setProgress(pct);
      if (pct >= 100) {
        clearInterval(intervalRef.current);
        nextStep();
      }
    }, 30);

    return () => clearInterval(intervalRef.current);
  }, [currentStep, isPaused, nextStep]);

  const step = steps[currentStep];
  const ScreenComponent = screens[step.id];

  return (
    <div style={{
      fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&display=swap');
        @keyframes fadeInDown {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.8s linear infinite; }
      `}</style>

      <div style={{ maxWidth: 540, width: "100%", margin: "0 auto" }}>
        {/* Browser chrome frame */}
        <div style={{
          borderRadius: 12,
          overflow: "hidden",
          border: "1px solid #d4d1cc",
          boxShadow: "0 8px 30px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.06)",
        }}>
          {/* Browser top bar */}
          <div style={{
            background: "#f1efeb",
            padding: "10px 14px",
            display: "flex", alignItems: "center", gap: 10,
            borderBottom: "1px solid #d4d1cc",
          }}>
            <div style={{ display: "flex", gap: 6 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#ee6a5f" }} />
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#f5bf4f" }} />
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#62c554" }} />
            </div>
            <div style={{
              flex: 1, background: "#fff", borderRadius: 6, padding: "4px 12px",
              fontSize: 11, color: C.textLight, textAlign: "center",
              border: "1px solid #e0ddd8",
            }}>
              stellarreply.com
            </div>
          </div>

          {/* App nav bar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "8px 16px",
            borderBottom: `1px solid ${C.border}`,
            background: "#fff",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
              <div style={{
                width: 18, height: 18, borderRadius: 5,
                background: `linear-gradient(135deg, ${C.green}, #059669)`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 9, color: "#fff", fontWeight: 700,
              }}>S</div>
              <span style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>StellarReply</span>
            </div>
            <div style={{ display: "flex", gap: 2 }}>
              {["Dashboard", "Reviews", "Settings"].map((t) => {
                const active =
                  (t === "Reviews" && ["import", "generate", "translate", "share"].includes(step.id)) ||
                  (t === "Dashboard" && step.id === "dashboard");
                return (
                  <span key={t} style={{
                    fontSize: 9.5, color: active ? C.green : C.textLight,
                    padding: "3px 6px", borderRadius: 4,
                    background: active ? C.greenBg : "transparent",
                    fontWeight: 600,
                  }}>{t}</span>
                );
              })}
            </div>
          </div>

          {/* Screen content */}
          <div style={{
            height: 320,
            background: C.bg,
            opacity: transitioning ? 0 : 1,
            transform: transitioning ? "scale(0.98)" : "scale(1)",
            transition: "all 0.28s ease",
            overflow: "hidden",
          }}>
            <ScreenComponent key={currentStep} />
          </div>
        </div>

        {/* Caption below the frame */}
        <div style={{
          textAlign: "center", marginTop: 20,
          opacity: transitioning ? 0 : 1,
          transform: transitioning ? "translateY(4px)" : "translateY(0)",
          transition: "all 0.28s ease",
          minHeight: 58,
        }}>
          <div style={{ color: C.text, fontSize: 15, fontWeight: 700, margin: "0 0 4px 0", lineHeight: 1.3 }}>
            {step.caption}
          </div>
          <p style={{ color: C.textMuted, fontSize: 12.5, margin: 0, lineHeight: 1.5 }}>
            {step.subtitle}
          </p>
        </div>

        <div style={{ display: "flex", gap: 4, marginTop: 14, padding: "0 4px" }}>
          {steps.map((s, i) => (
            <div
              key={s.id}
              onClick={() => goToStep(i)}
              style={{
                flex: 1, height: 3, borderRadius: 2,
                background: "#e0ddd8", cursor: "pointer",
                overflow: "hidden", position: "relative",
              }}
            >
              <div style={{
                position: "absolute", top: 0, left: 0, height: "100%",
                borderRadius: 2,
                background: i <= currentStep ? C.green : "transparent",
                width: i < currentStep ? "100%" : i === currentStep ? `${progress}%` : "0%",
                transition: i === currentStep ? "none" : "width 0.3s ease",
              }} />
            </div>
          ))}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 14, marginTop: 10 }}>
          {steps.map((s, i) => (
            <span
              key={s.id}
              onClick={() => goToStep(i)}
              style={{
                fontSize: 10, fontWeight: 600, cursor: "pointer",
                color: i === currentStep ? C.green : C.textLight,
                transition: "color 0.2s ease",
              }}
            >
              {s.label}
            </span>
          ))}
          <span style={{ color: "#d4d1cc", fontSize: 10 }}>|</span>
          <span
            onClick={() => setIsPaused((p) => !p)}
            style={{
              fontSize: 10, cursor: "pointer", fontWeight: 600,
              color: isPaused ? C.green : C.textLight,
              transition: "all 0.2s ease",
            }}
          >
            {isPaused ? "Resume" : "Pause"}
          </span>
        </div>
      </div>
    </div>
  );
}
